import React, { Fragment } from "react";
import {
  Flex,
  Button,
  Center,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Text,
  Box,
  useColorModeValue,
} from "@chakra-ui/react";
import { CanAccess, CrudFilter, useNavigation } from "@refinedev/core";
import { ListFilterPlus, X, XCircle } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { SearchTextfield } from "./fields";
import { useLocation, useNavigate } from "react-router";
import { capitalizeFirst, toTitleCase } from "../utils/toTitleCase";

type FilterComponentProps = {
  filters: (value: CrudFilter[]) => void;
  children?: (form: {
    register: ReturnType<typeof useForm>["register"];
    errors: ReturnType<typeof useForm>["formState"]["errors"];
    control: ReturnType<typeof useForm>["control"];
  }) => React.ReactNode;
  CreatePath?: string;
  headerOptions?: React.ReactNode[];
  rbacResource?: string;
  createName?: string;
};

export const FilterComponent = ({
  filters,
  children,
  CreatePath,
  rbacResource,
  createName,
  headerOptions,
}: FilterComponentProps) => {
  const { create } = useNavigation();
  const [filterData, setFilterData] = useState<CrudFilter[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
  } = useForm();

  const applyModalFilters = (data: any) => {
    const updatedFilters: CrudFilter[] = [];

    for (const key in data) {
      const value = data[key];

      if (Array.isArray(value) && value.length > 0) {
        updatedFilters.push({
          field: key,
          operator: "in",
          value,
        });
      } else if (value !== undefined && value !== null && value !== "") {
        updatedFilters.push({
          field: key,
          operator: "eq",
          value,
        });
      }
    }

    setFilterData((prev) => {
      const remainingFilters = prev.filter(
        (f: any) => !data.hasOwnProperty(f.field)
      );
      const combined = [...remainingFilters, ...updatedFilters];
      filters(combined);
      return combined;
    });

    onClose();
  };

  const onClose = () => {
    setIsModalOpen(false);
    reset();
  };

  const openModal = () => {
    const defaultValues: { [key: string]: any } = {};
    filterData.forEach((f: any) => {
      defaultValues[f.field] = f.value;
    });
    reset(defaultValues);
    setIsModalOpen(true);
  };

  const tagBg = useColorModeValue("blue.100", "blue.800");
  const tagColor = useColorModeValue("blue.800", "blue.100");
  return (
    <>
      <Flex
        mb={4}
        justifyContent="space-between"
        alignItems="center"
        gap={8}
        py={2}
      >
        <Flex align="center" gap={5}>
          <SearchTextfield
            onChange={(value) => {
              const newFilter = {
                field: "search",
                operator: "eq",
                value,
              };
              const filterIndex = filterData.findIndex(
                (f: any) => f.field === "search"
              );
              const updatedFilters = [...filterData];
              if (filterIndex !== -1) {
                updatedFilters[filterIndex] = newFilter as any;
              } else {
                updatedFilters.push(newFilter as any);
              }
              setFilterData(updatedFilters);
              filters(updatedFilters);
            }}
          />

          <Flex align="center" gap={3}>
            {children ? (
              <>
                <Button
                  color={"primaryColor"}
                  onClick={openModal}
                  size="sm"
                  rightIcon={<ListFilterPlus size={16} />}
                >
                  Filters
                </Button>

                <Center height="40px">
                  <Divider orientation="vertical" color={"white"} />
                </Center>
              </>
            ) : (
              <></>
            )}
            <CanAccess
              resource={rbacResource ?? capitalizeFirst(pathSegments[0])}
              action="Create"
              fallback={null}
            >
              <Button
                colorScheme="blue"
                onClick={() => {
                  CreatePath
                    ? navigate(`${CreatePath}`)
                    : create(capitalizeFirst(pathSegments[0]));
                }}
                size="sm"
              >
                {createName
                  ? createName
                  : `Create ${toTitleCase(pathSegments[0])}`}
              </Button>
            </CanAccess>
            {headerOptions?.map((item, index) => (
              <React.Fragment key={index}>{item}</React.Fragment>
            ))}
          </Flex>
        </Flex>
      </Flex>

      {filterData.filter((data: any) => data.field !== "search").length > 0 && (
        <Box bg={"boxBg"} py={4} px={5} mb={5} rounded={"md"}>
          <Flex align="start" gap={2} flexDirection={"column"}>
            <Text fontWeight={"bold"} fontSize={13}>
              Applied filters:
            </Text>
            <Flex align={"center"} gap={2} wrap="wrap">
              {filterData
                .filter((data: any) => data.field !== "search")
                .flatMap((data: any) => {
                  const isArray = Array.isArray(data.value);
                  const values = isArray ? data.value : [data.value];

                  return values.map((val: string, valIdx: number) => ({
                    field: data.field,
                    value: val,
                    operator: data.operator,
                    isPartOfArray: isArray,
                    totalArrayLength: values.length,
                    originalData: data,
                    valueIndex: valIdx,
                  }));
                })
                .map((item: any, idx: number) => (
                  <Flex
                    key={`${item.field}-${item.value}-${idx}`}
                    flexDirection={"column"}
                    background={tagBg}
                    color={tagColor}
                    py={1}
                    pl={4}
                    pr={1}
                    rounded={"lg"}
                    position="relative"
                  >
                    <Flex
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <Text fontSize={"14px"} fontWeight={"bold"} mr={2}>
                        {toTitleCase(item.value)}
                      </Text>
                      <Button
                        aria-label="Delete filter"
                        size="xs"
                        variant="ghost"
                        colorScheme="black"
                        onClick={() => {
                          let updatedFilters;

                          if (item.isPartOfArray) {
                            // Remove one value from array
                            const remainingValues =
                              item.originalData.value.filter(
                                (_: string, i: number) => i !== item.valueIndex
                              );

                            if (remainingValues.length > 0) {
                              updatedFilters = filterData.map((f: any) =>
                                f.field === item.field
                                  ? { ...f, value: remainingValues }
                                  : f
                              );
                            } else {
                              // If array becomes empty, remove the filter
                              updatedFilters = filterData.filter(
                                (f: any) => f.field !== item.field
                              );
                            }
                          } else {
                            updatedFilters = filterData.filter(
                              (f: any) =>
                                !(
                                  f.field === item.field &&
                                  f.value === item.value
                                )
                            );
                          }

                          setFilterData(updatedFilters);
                          filters(updatedFilters);
                        }}
                      >
                        <X size={15} />
                      </Button>
                    </Flex>
                  </Flex>
                ))}
              <Button
                colorScheme="red"
                size="sm"
                variant={"ghost"}
                leftIcon={<XCircle size={16} />}
                onClick={() => {
                  setFilterData([]);
                  filters([]);
                }}
              >
                Clear
              </Button>
            </Flex>
          </Flex>
        </Box>
      )}

      <Modal isOpen={isModalOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent as="form" onSubmit={handleSubmit(applyModalFilters)}>
          <ModalHeader>Filters</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex
              flexDirection={"column"}
              gap={3}
              overflowY={"auto"}
              maxH={"60vh"}
            >
              {children ? children({ register, errors, control }) : <></>}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" type="submit">
              Save Filter
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
